import React, {useEffect, useState} from "react"
import {Button, Drawer, Input, Layout, message, Select, Space, Table, Tag} from "antd"
import {DownloadOutlined} from "@ant-design/icons"
import type {ColumnsType} from "antd/es/table"
import {useDispatch, useSelector} from "react-redux"
import ShowOrdersInfo from "./ShowOrdersInfo"
import global from "../../global"

const {Search} = Input

/**
 * 定义ShowOrderType
 * @constructor
 */
interface ShowOrderType {
  showName: string
  orderNo: string
  orderType: string
  price: number
  name: string
  mobile: string
  province: string
  amount: number
  payStatus: string
  orderStatus: string
  remark: string
  seats: String[]
}

const ShowOrdersTable: React.FC = () => {
  const [infoOpen, setInfoOpen] = useState(false)
  const [currentOrderNo, setCurrentOrderNo] = useState("")

  const showOrders = useSelector((state: any) => state.showOrders)
  const dispatch = useDispatch()

  const [messageApi, contextMessageHolder] = message.useMessage()

  const columns: ColumnsType<ShowOrderType> = [
    {
      title: "订单号",
      dataIndex: "orderNo",
      key: "orderNo",
    },
    {
      title: "演出名称",
      dataIndex: "showName",
      key: "showName",
    },
    {
      title: "选手姓名",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "手机号码",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "参演省份",
      dataIndex: "province",
      key: "province",
    },
    {
      title: "订单总价",
      dataIndex: "price",
      key: "price",
      render: (text: number) => `¥${text/100}`
    },
    {
      title: "实付金额",
      dataIndex: "amount",
      key: "amount",
      render: (text: number) => `¥${text/100}`
    },
    {
      title: "支付状态",
      dataIndex: "payStatus",
      key: "payStatus",
      render: (text: string) => {
        const color = global.getPayStatusColor(text)
        const statusText = global.getPayStatusText(text)
        return <Tag color={color}>{statusText}</Tag>
      }
    },
    {
      title: "订单状态",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (text: string) => {
        const color = global.getOrderStatusColor(text)
        const statusText = global.getOrderStatusText(text)
        return <Tag color={color}>{statusText}</Tag>
      }
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => {
        return (
          <>
            <Button type="link" size={"small"} onClick={() => handleInfo(record)}>
              查看详情
            </Button>
          </>
        )
      },
    },
  ]

  const onSearch = (value: string) => {
    let request = showOrders.request
    request.keywords = value
    request.pageNum = 1
    dispatch.showOrders.setData({request})
  }

  const handleInfo = (record: ShowOrderType) => {
    setCurrentOrderNo(record.orderNo)
    setInfoOpen(true)
  }

  const onInfoClose = () => {
    setInfoOpen(false)
    setCurrentOrderNo("")
  }

  const setPayStatus = (payStatus: string) => {
    let request = showOrders.request
    request.payStatus = payStatus
    request.pageNum = 1
    dispatch.showOrders.setData({request})
  }

  const setShow = (showId: string) => {
    let request = showOrders.request
    request.showId = showId
    request.pageNum = 1
    dispatch.showOrders.setData({request})
  }

  const handleDownload = async () => {
    try {
      await dispatch.showOrders.download()
      messageApi.success("下载成功")
    } catch (error) {
      console.error('Download error:', error)
      messageApi.error("下载失败")
    }
  }

  const showOptions = [
    {label: "全部", value: ""},
    ...showOrders.shows.map((show: any) => ({
      label: show.name,
      value: show.id
    }))
  ]

  useEffect(() => {
    dispatch.showOrders.getShows()
  }, [dispatch.showOrders])

  useEffect(() => {
    dispatch.showOrders.getPage()
  }, [showOrders.request.keywords, showOrders.request.pageNum, showOrders.request.payStatus, showOrders.request.showId, dispatch.showOrders])

  return (
    <>
      {contextMessageHolder}
      <Layout>
        <Space>
          <Select style={{width: 350}} onChange={(showId: string) => setShow(showId)} options={showOptions} placeholder="选择演出"/>
          <Select style={{width: 120}} onChange={(payStatus: string) => setPayStatus(payStatus)} options={global.getPayStatusOptions()} placeholder="付款状态"/>
          <Search placeholder="输入查询内容" enterButton="查询" onSearch={onSearch}/>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
            下载
          </Button>
        </Space>
        <Layout style={{marginTop: "10px"}}>
          <Table
            columns={columns}
            dataSource={showOrders.data.list}
            pagination={{
              pageSize: showOrders.data.pageSize,
              total: showOrders.data.total,
              onChange: (page, pageSize) => {
                let request = {...showOrders.request, pageNum: page, pageSize}
                dispatch.showOrders.setData({request})
              },
            }}
          />
        </Layout>
      </Layout>

      <Drawer
        title="演出订单详情"
        width={500}
        open={infoOpen}
        onClose={onInfoClose}
        styles={{body: {paddingBottom: 80}}}
      >
        {currentOrderNo && (
          <ShowOrdersInfo 
            orderNo={currentOrderNo} 
            onClose={onInfoClose}
            onRefundSuccess={() => {
              messageApi.success("退款成功")
              dispatch.showOrders.getPage()
            }}
          />
        )}
      </Drawer>
    </>
  )
}

export default ShowOrdersTable 